/**
 * Shared presentational pieces for the Capabilities surfaces (agent/hive/tenant).
 *
 * ISS-572 (REQ-CAP-002 §3): textos por slug vía i18n del frontend (en/es) con
 * fallback a los metadatos del backend, icono renderizado desde `ui.icon` y
 * categoría traducida.
 */

import type { ComponentType, ReactNode } from 'react';
import { BookOpen, FileText, Globe, Image, Package, Puzzle } from 'lucide-react';
import { Badge, Card } from '@/components/ui';
import { useI18n } from '@/i18n';
import type { CapabilityCatalogEntry, CapabilityOrigin, EffectiveCapability } from './types';

const ORIGIN_VARIANT: Record<CapabilityOrigin, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  agent: 'default',
  hive: 'secondary',
  tenant: 'outline',
  platform: 'outline',
};

/** Iconos soportados por el metadato `ui.icon` del registro (fallback: Puzzle). */
const CAPABILITY_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  globe: Globe,
  'file-text': FileText,
  'book-open': BookOpen,
  image: Image,
  package: Package,
};

export function CapabilityIcon({
  entry,
  className,
}: {
  entry: CapabilityCatalogEntry;
  className?: string;
}) {
  const Icon = CAPABILITY_ICONS[entry.ui?.icon ?? ''] ?? Puzzle;
  return <Icon className={className ?? 'h-4 w-4'} />;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** ISS-572: título por slug con fallback a `ui.title` / `display_name` del backend. */
export function capabilityTitle(t: Translate, entry: CapabilityCatalogEntry): string {
  const key = `admin.capabilities.catalog.${entry.slug}.title`;
  const value = t(key);
  return value === key ? entry.ui?.title || entry.display_name : value;
}

/** ISS-572: descripción por slug con fallback a `ui.description` / `description`. */
export function capabilityDescription(t: Translate, entry: CapabilityCatalogEntry): string {
  const key = `admin.capabilities.catalog.${entry.slug}.description`;
  const value = t(key);
  return value === key ? entry.ui?.description || entry.description : value;
}

/** ISS-572: categoría traducida (fallback al slug del backend). */
export function capabilityCategory(t: Translate, entry: CapabilityCatalogEntry): string {
  const key = `admin.capabilities.category.${entry.category}`;
  const value = t(key);
  return value === key ? entry.category : value;
}

export function OriginBadge({ origin }: { origin: CapabilityOrigin }) {
  const { t } = useI18n();
  const label = t(`admin.capabilities.origin.${origin}`) || origin;
  return (
    <Badge variant={ORIGIN_VARIANT[origin] ?? 'outline'} title={label}>
      {label}
    </Badge>
  );
}

interface RowProps {
  entry: CapabilityCatalogEntry;
  effective?: EffectiveCapability;
  /** Control de configuración (check/select…) del nivel correspondiente. */
  control?: ReactNode;
  /** Panel expandible de settings (formulario o resumen read-only). */
  extra?: ReactNode;
}

export function CapabilityRow({ entry, effective, control, extra }: RowProps) {
  const { t } = useI18n();
  const unavailable = entry.platform_active === false;
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            <CapabilityIcon entry={entry} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {capabilityTitle(t, entry)}
              </span>
              <Badge variant="outline">{capabilityCategory(t, entry)}</Badge>
              {effective && <OriginBadge origin={effective.origin} />}
              {unavailable && (
                <Badge variant="destructive">
                  {t('admin.capabilities.platformOff') || 'No disponible en la plataforma'}
                </Badge>
              )}
            </div>
            {capabilityDescription(t, entry) && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {capabilityDescription(t, entry)}
              </p>
            )}
          </div>
        </div>
        <div className="shrink-0 pt-0.5">{control}</div>
      </div>
      {extra && <div className="mt-3">{extra}</div>}
    </Card>
  );
}
